import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { SsSelectTooltipDirective } from '../../shared/ss-select-tooltip.directive';

import type { TableCellRule, TableCellRuleOp } from '../../../chart-components/unified-table-matrix-chart/table-cell-config';
import type { LabelValueOption } from '../../shared/self-service-config.types';
import { DvmBuilderStepBase, TileRecomputeResult } from '../../dvm-layout-builder/dvm-builder-host';
import type { DashboardTile } from '../../dvm-layout-builder/dvm-layout-builder.types';
import { toKpiNumberFormat, toNumberScale } from '../../dvm-layout-builder/dvm-builder-number-format.utils';
import { buildKpiWidgetData, kpiDecorationData } from './kpi-widget.utils';

/**
 * KPI card build form: one dataset column + aggregation (or a formula) that
 * becomes a single headline value. The shell owns title / dataset / targets /
 * the Format accordion; this step owns the KPI-specific fields and builds the
 * KPI part of the generate payload.
 */
@Component({
  selector: 'app-kpi-step',
  standalone: true,
  imports: [CommonModule, FormsModule, NgSelectModule, SsSelectTooltipDirective],
  templateUrl: './kpi-step.component.html',
  styles: [`
    :host { display: contents; }
    .kpi-rules-head { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
    .kpi-rules-head label { flex: 1 1 auto; min-width: 0; margin: 0; }
    .kpi-rules-add { font-size: 12px; padding: 3px 10px; border: 1px solid rgba(39, 125, 242, .35); border-radius: 6px; background: rgba(39, 125, 242, .08); color: #1f6feb; cursor: pointer; }
    .kpi-rules-add:hover { background: rgba(39, 125, 242, .16); }
    .kpi-rule { display: flex; flex-direction: column; gap: 6px; min-width: 0; margin-top: 6px; padding: 6px 8px; border: 1px solid rgba(120, 140, 170, .3); border-radius: 8px; background: rgba(248, 250, 252, .9); }
    .kpi-rule-line { display: flex; align-items: center; gap: 6px; min-width: 0; }
    .kpi-rule-line-2 { flex-wrap: wrap; padding-left: 24px; }
    .kpi-rule-op { flex: 1 1 auto; min-width: 0; width: auto; }
    .kpi-rule-value { flex: 1 1 90px; }
    .kpi-rule-index { font-size: 11px; font-weight: 700; color: #1f6feb; background: rgba(39, 125, 242, .1); border-radius: 999px; width: 18px; height: 18px; display: inline-flex; align-items: center; justify-content: center; }
    .kpi-rule input[type='text'] { width: auto; min-width: 0; font-size: 12px; padding: 4px 6px; }
    .kpi-rule-color { display: inline-flex; align-items: center; gap: 6px; }
    .kpi-rule-color input[type='color'] { width: 30px; height: 24px; padding: 0; border: 1px solid rgba(120, 140, 170, .4); border-radius: 4px; background: transparent; cursor: pointer; }
    .kpi-rule-hex { font-size: 11px; color: #4e5f78; font-family: monospace; }
    .kpi-rule-actions { display: inline-flex; gap: 2px; margin-left: auto; }
    .kpi-rule-x { font-size: 11px; line-height: 1; padding: 3px 5px; border: 1px solid transparent; border-radius: 4px; background: transparent; color: #4e5f78; cursor: pointer; }
    .kpi-rule-x:hover:not(:disabled) { background: rgba(120, 140, 170, .18); }
    .kpi-rule-x:disabled { opacity: .35; cursor: default; }
    .kpi-rule-x-del:hover:not(:disabled) { color: #b42318; background: rgba(180, 35, 24, .1); }
  `],
})
export class KpiStepComponent extends DvmBuilderStepBase {
  readonly layoutTypes = ['KPI'] as const;

  kpiField = '';
  kpiAggregation = 'SUM';
  kpiFormula = '';

  /** Conditions a KPI value can be tested with (numeric subset of the table
   *  cell-rule operators, in the same order the table editor lists them). */
  readonly valueRuleOperators: LabelValueOption[] = [
    { label: 'Less than', value: '<' },
    { label: 'Less than or equal', value: '<=' },
    { label: 'Greater than', value: '>' },
    { label: 'Greater than or equal', value: '>=' },
    { label: 'Equal to', value: '=' },
    { label: 'Not equal to', value: '!=' },
    { label: 'Between', value: 'between' },
    { label: 'Is empty', value: 'empty' },
    { label: 'Any value (catch-all)', value: 'any' },
  ];

  get kpiAggregations(): LabelValueOption[] {
    return this.host.layout.options?.kpiAggregations?.length
      ? this.host.layout.options.kpiAggregations
      : [
          { label: 'Sum', value: 'SUM' },
          { label: 'Average', value: 'AVG' },
          { label: 'Count', value: 'COUNT' },
          { label: 'Distinct Count', value: 'COUNT_DISTINCT' },
          { label: 'Minimum', value: 'MIN' },
          { label: 'Maximum', value: 'MAX' },
        ];
  }

  /** A formula (or typed references) replaces Column + Aggregation — and needs no dataset. */
  private get usesFormula(): boolean {
    return !!this.kpiFormula.trim() || this.host.targetReferences(this.host.componentId).length > 0;
  }

  onKpiFieldDrop(event: DragEvent): void {
    const mapping = this.host.readFieldDrop(event);
    if (!mapping) return;
    this.host.useDroppedSingleDataset(mapping.dataset);
    this.kpiField = mapping.field;
    this.host.invalidatePreview();
  }

  onKpiFormulaFieldDrop(event: DragEvent): void {
    const mapping = this.host.readFieldDrop(event);
    if (!mapping) return;
    this.host.useDroppedSingleDataset(mapping.dataset);
    this.kpiFormula = this.host.appendFormulaToken(this.kpiFormula, mapping.field);
    this.host.invalidatePreview();
  }

  // ── Value colour rules (stored on the tile format as `valueRules`) ──────
  // Same rule shape + matcher as table cell formatting (table-cell-config.ts):
  // the card colours its value by the FIRST matching rule, so "< 20000 →
  // yellow" is one rule. Lives on `host.barFormat` so it rides along with the
  // other Format values into the tile, the published dashboard and Edit.

  get valueRules(): TableCellRule[] {
    const raw = this.host.barFormat['valueRules'];
    return Array.isArray(raw) ? (raw as TableCellRule[]) : [];
  }

  private writeValueRules(rules: TableCellRule[]): void {
    if (rules.length) this.host.barFormat['valueRules'] = rules;
    else delete this.host.barFormat['valueRules'];
    this.host.onBarFormatChange();
  }

  addValueRule(): void {
    this.writeValueRules([...this.valueRules, { op: '<', value: '', color: '#f59e0b' }]);
  }

  removeValueRule(index: number): void {
    this.writeValueRules(this.valueRules.filter((_, i) => i !== index));
  }

  moveValueRule(index: number, delta: number): void {
    const rules = [...this.valueRules];
    const target = index + delta;
    if (target < 0 || target >= rules.length) return;
    [rules[index], rules[target]] = [rules[target], rules[index]];
    this.writeValueRules(rules);
  }

  /** Edits the rule IN PLACE (the ngFor keeps its DOM, so a text input keeps
   *  focus while typing) and tells the shell the format changed. */
  setValueRule(rule: TableCellRule, key: 'op' | 'value' | 'value2' | 'color', value: unknown): void {
    if (key === 'op') rule.op = (value as TableCellRuleOp) || '<';
    else if (key === 'value' || key === 'value2') rule[key] = value === null || value === undefined ? '' : String(value);
    else rule.color = String(value ?? '');
    this.host.onBarFormatChange();
  }

  ruleTakesValue(rule: TableCellRule): boolean {
    return rule.op !== 'empty' && rule.op !== 'notEmpty' && rule.op !== 'any';
  }

  // ── DvmBuilderStep ──────────────────────────────────────────────────────

  blockers(): string[] {
    if (this.usesFormula) return [];
    if (!this.host.selectedDatasets.length) return ['Dataset'];
    const missing: string[] = [];
    if (!this.kpiField) missing.push('Column');
    if (!this.kpiAggregation) missing.push('Aggregation');
    if (missing.length) missing.push('or a formula instead');
    return missing;
  }

  buildPayload(): Record<string, unknown> {
    const kpiFormula = this.kpiFormula.trim();
    const kpiReferences = this.host.referencesForFormula(this.host.componentId, kpiFormula);
    return {
      componentId: this.host.componentId,
      dashboardId: this.host.dashboardId,
      componentType: 'CHART',
      chartType: this.host.selectedLayoutType, // 'KPI'
      title: this.host.title.trim(),
      dataset: this.host.drillDataset,
      field: this.kpiField,
      aggregation: this.kpiAggregation,
      ...(kpiFormula ? { formula: kpiFormula } : {}),
      ...(kpiReferences ? { references: kpiReferences } : {}),
      ...this.host.chartTargetsPayload(),
      ...this.host.headerFormatPayload(),
    };
  }

  loadFromTile(tile: DashboardTile, payload: Record<string, unknown>): void {
    this.kpiField = String(payload['field'] ?? '');
    this.kpiAggregation = String(payload['aggregation'] ?? 'SUM');
    this.kpiFormula = String(payload['formula'] ?? '');
    this.host.rehydrateOwnFormulaReferences(this.host.componentId, payload['references']);
    this.host.barFormat = { ...(tile.format || {}) };
  }

  reset(): void {
    this.kpiField = '';
    this.kpiAggregation = 'SUM';
    this.kpiFormula = '';
  }

  /** Widget data for a freshly generated KPI tile (the shell adds it to the canvas). */
  buildWidgetData(response: Record<string, unknown> | null, tileId: string, format?: Record<string, unknown>): Record<string, unknown> {
    return buildKpiWidgetData(response, { tileId, title: this.host.title, format, preferredKey: this.kpiField });
  }

  // ── Tile-side hooks (DvmBuilderStep) ──────────────────────────────────────

  tileFromResponse(base: DashboardTile, response: Record<string, unknown> | null): DashboardTile {
    return { ...base, colSpan: 3, rowSpan: 3, kpiData: this.buildWidgetData(response, base.id, base.format) };
  }

  applyFormatToTile(tile: DashboardTile, format: Record<string, unknown>): void {
    if (!tile.kpiData) return;
    tile.kpiData = {
      ...tile.kpiData,
      title: (format['title'] as string) || tile.title,
      numberFormat: toKpiNumberFormat(format),
      numberScale: toNumberScale(format['numberFormat']),
      decimals: Number(format['decimals'] ?? 1),
      prefix: (format['prefix'] as string) || '',
      suffix: (format['suffix'] as string) || '',
      infoText: (format['infoText'] as string) || '',
      infoIconPosition: (format['infoIconPosition'] as string) || 'corner',
      alignment: (format['contentAlignment'] as string) || 'center',
      cardLayout: (format['cardLayout'] as string) || '',
      titleFontSize: format['titleFontSize'] ?? '',
      valueFontSize: format['valueFontSize'] ?? '',
      ...kpiDecorationData(format),
    };
  }

  applyRecomputeResult(tile: DashboardTile, { result }: TileRecomputeResult): boolean {
    tile.kpiData = buildKpiWidgetData(result, { tileId: tile.id, title: tile.title, format: tile.format, preferredKey: String(tile.payload?.['field'] ?? '') });
    return true;
  }
}
